import React, { Fragment, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import { Container } from 'react-bootstrap';
import { useTheme } from '@mui/material';
import SvgIcon from '../../../Helpers/Svg/SvgIcon';
import "./Education.scss";

export default function Education({ data, customData }) {
  const [selectedCategory, setSelectedCategory] = useState(0);
  const currentData = !customData ? data : customData;
  const categoryElementRef = useRef([]);
  const[ selectCategoryTriggered, setSelectCategoryTriggered] = useState(false);

  const handleSelectedCategory = (category) => {
    if (!selectCategoryTriggered) {
      setSelectCategoryTriggered(true);
    }
    
    setSelectedCategory(category);
  };
  
  useEffect(() => {
    const isMobile = (window.innerWidth <= 992.5);
    if (isMobile && selectCategoryTriggered) {
      window.scrollTo({top: categoryElementRef.current[selectedCategory].offsetTop - 100, left: 0, behavior: "smooth"});
    }
  }, [selectedCategory]);

  return (
    <div className="education-wrapper">
      <Container>
        <div className="education-container tabbed">
          <EducationContent 
            handleSelectedCategory={handleSelectedCategory}
            selectedCategory={selectedCategory} 
            categories={currentData} />
        </div>
        <div className="education-container stacked">
          <EducationMobile
            handleSelectedCategory={handleSelectedCategory}
            selectedCategory={selectedCategory} 
            categories={currentData} 
            categoryRef={categoryElementRef}/>
        </div>
      </Container>
    </div>);
}

export function EducationTabHeader({topics, handleSelectedCategory, selectedCategory}) {
  const theme = useTheme();

  return (
    <div className="education-tab-header-container">
      <ul>
        {topics.map((topic, i) => {
            return (
              <li className={"selectedCategory" + (selectedCategory === i ? " selected" : "")} onClick={() => handleSelectedCategory(i)} key={(`tab${i}`)}>
                <h3><SvgIcon viewBox={"0 0 40 40"} iconName={topic.iconName} color={theme.palette.primary.main} /><span>{topic.title}</span></h3>
                <p>{topic.description}</p>
              </li>
            )
          })}
      </ul>
    </div>);
}

export function EducationContent({categories, handleSelectedCategory, selectedCategory}) {
  const [selectedTopic, setSelectedTopic] = useState(0);

  const handleSelectedTopic = (topic) => {
    setSelectedTopic(topic);
  };

  useEffect(() => {
    setSelectedTopic(0);
  }, [selectedCategory]);

  return (
    <Fragment>
      <EducationTabHeader handleSelectedCategory={handleSelectedCategory} selectedCategory={selectedCategory} topics={categories} />
      <div className="education-content-container">
        {categories.map((category, i) => {
          return (
            (selectedCategory === i) &&
            <Fragment key={category}>
              <div className="education-content-header">
                <h2>{category.title}</h2>
              </div>
              <div className="topic-grid">
                <div className="topic-list-container">
                  <h3>Explore Topics</h3>
                  <ul>
                    {category.topics.map((topic, i) => {
                      return <li key={(`list${i}`)}><button type="button" className={"selectedTopic" + (selectedTopic === i ? " selected" : "")} onClick={() => handleSelectedTopic(i)}>{topic.title}</button></li>
                    })}
                  </ul>
                </div>
                <div className="topic-content-container">
                    {category.topics.map((topic, i) => {
                      return (selectedTopic === i) && 
                        <div className="topic-content" key={(`content${i}`)}>
                          <h2>{topic.title}</h2>
                          {topic.values.map((value, i) => { 
                            return (
                              <Fragment key={(`value${i}`)}>
                                <h3>{value.title}</h3>
                                <p>{value.description}</p>
                              </Fragment>)
                          })}
                        </div>
                    })}
                </div>
              </div>
            </Fragment>)
        })}
      </div>
    </Fragment>);
}

function EducationMobile({categories, handleSelectedCategory, selectedCategory, categoryRef}) {
  const [selectedTopic, setSelectedTopic] = useState(null);
  const theme = useTheme();
  const topicElementRef = useRef([]);
  const[ selectTopicTriggered, setSelectTopicTriggered] = useState(false);

  const handleSelectedTopic = (topic) => {
    if (!selectTopicTriggered) {
      setSelectTopicTriggered(true);
    }

    if (topic === selectedTopic) {
      setSelectedTopic(null);
    } else {
      setSelectedTopic(topic);
    }
  };
  
  useEffect(() => {
    setSelectedTopic(null);
  }, [selectedCategory]);
  
  useEffect(() => {
    if (selectedTopic !== null && selectTopicTriggered) {
      window.scrollTo({top: (topicElementRef.current[selectedTopic].offsetTop - 100), left: 0, behavior: "smooth"});
    }
  }, [selectedTopic]);

  return (
    <div className="education-mobile-container">
      <ul>
        {categories.map((category, i) => {
            return (
              <li className={"selectedCategoryMobile" + (selectedCategory === i ? " selected" : "")} onClick={() => handleSelectedCategory(i)} key={(`tab${i}`)} ref={(e) => (categoryRef.current[i] = e)}>
                <div className="tab-content">
                  <h3><SvgIcon viewBox={"0 0 40 40"} iconName={category.iconName} color={theme.palette.primary.main} /><span>{category.title}</span></h3>
                  <p>{category.description}</p>
                </div>
                {(selectedCategory === i) && <ul className="category-group-container">
                  {category.topics.map((topic, index) => {
                    return (
                      <Fragment key={`topic${index}`}>
                        <li className="topic-content-container">
                          <button type="button" className={"selectedTopicMobile" + (selectedTopic === index ? " selected" : "")} onClick={() => handleSelectedTopic(index)} ref={(e) => (topicElementRef.current[index] = e)}>{topic.title}</button>
                        </li>
                        {(selectedCategory === i) && (selectedTopic === index) && <li className="topic-content-group">
                          <div className="topic-group">
                            <h2>{topic.title}</h2>
                            {topic.values.map((value, index) => {
                              return (
                                <div className="topic-content" key={`value${index}`}>
                                  <h3>{value.title}</h3>
                                  <p>{value.description}</p>
                                </div>)
                            })}
                          </div>
                        </li>}
                      </Fragment>
                    )
                  })}
                </ul>}
              </li>)
          })}
      </ul>
    </div>
  );
}

Education.propTypes = {
  data: PropTypes.array.isRequired,
  customData: PropTypes.array,
}

EducationTabHeader.propTypes = {
  handleSelectedCategory: PropTypes.func.isRequired,
  selectedCategory: PropTypes.number.isRequired,
  topics: PropTypes.array.isRequired,
}

EducationContent.propTypes = {
  handleSelectedCategory: PropTypes.func.isRequired,
  selectedCategory: PropTypes.number.isRequired,
  categories: PropTypes.array.isRequired,
}

EducationMobile.propTypes = {
  handleSelectedCategory: PropTypes.func.isRequired,
  selectedCategory: PropTypes.number.isRequired,
  categories: PropTypes.array.isRequired,
}